import React, { useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useServiceCatalog, useTriageServiceRequest } from './serviceRequestHooks';

export default function TriageModal({ publicId, onClose, onTriaged }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data: catalogData } = useServiceCatalog();
  const triage = useTriageServiceRequest(publicId);

  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');

  const catalog = catalogData?.catalog || [];

  const submit = async () => {
    if (selected.length === 0) {
      toast.error('Pick at least one service item to route this request.');
      return;
    }
    try {
      await triage.mutateAsync({ service_item_slugs: selected, note });
      toast.success('Triaged.');
      onTriaged?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Could not triage this request.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className={`max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium font-rasa">Triage request</h3>
            <p className={`text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Route this unsure request to the right team.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {catalog.map((g) => (
            <div key={g.department.slug}>
              <div className={`text-xs uppercase tracking-wide font-rasa mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {g.department.name}
              </div>
              {g.service_areas.map((area) => (
                <div key={area.service_area.slug} className="mb-3">
                  <div className={`text-xs font-rasa mb-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{area.service_area.name}</div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {area.items.map((item) => {
                      const checked = selected.includes(item.slug);
                      return (
                        <label key={item.slug}
                               className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer text-sm font-rasa ${
                                 checked
                                   ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                   : isDark ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                               }`}>
                          <input
                            type="checkbox"
                            className="mt-1 accent-blue-500"
                            checked={checked}
                            onChange={(e) =>
                              setSelected((prev) =>
                                e.target.checked ? [...prev, item.slug] : prev.filter((s) => s !== item.slug)
                              )
                            }
                          />
                          {item.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div>
            <label className={`block text-xs uppercase tracking-wide font-rasa mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Triage note (internal)
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={`w-full rounded-md border px-3 py-2 text-sm font-rasa ${
                isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Why you routed it this way (optional)"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
                    className={`px-3 py-1.5 text-sm font-rasa rounded-md ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={triage.isPending}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-rasa rounded-md bg-blue-500 text-white hover:bg-blue-600">
              <ArrowRight size={14} /> {triage.isPending ? 'Routing…' : 'Route request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
